import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Phone, Mail, MapPin, Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data/mock';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Design Ideas', href: '/design-ideas' },
    { name: 'About Us', href: '/about' },
    { name: 'Packages', href: '/packages' },
    { name: 'Contact', href: '/contact' },
    { name: 'Blog', href: '/blog' },
  ];

  const services = [
    { name: 'Turnkey Interiors', href: '/services/turnkey-interiors' },
    { name: 'Bedroom Interiors', href: '/services/bedroom-interiors' },
    { name: 'Modular Kitchen', href: '/services/modular-kitchen' },
    { name: 'Customized Kitchen', href: '/services/customized-kitchen' },
    { name: 'Home Construction', href: '/construction' },
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/myinstaspaces/', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/myinstaspaces', label: 'Instagram' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  return (
    <footer className="bg-gray-900 text-white relative">
      {/* Premium CTA Section */}
      <div className="relative py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-10 md:p-16 text-center bg-brand-primary text-white rounded-[3rem] shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>

            <div className="relative z-10">
              <h3 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Space?
              </h3>
              <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
                Let's discuss your project and create something beautiful together. Get in touch for a free consultation.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-brand-primary hover:bg-brand-light px-10 py-8 text-xl rounded-full shadow-xl transition-all duration-300">
                    Schedule Consultation
                  </Button>
                </Link>
                <a href="https://wa.me/918147813339" target="_blank" rel="noreferrer">
                  <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 px-10 py-8 text-xl rounded-full transition-all duration-300">
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <img
                src={companyInfo.logo}
                alt={companyInfo.name}
                className="h-14 w-auto mb-6"
              />
              <p className="text-gray-400 leading-relaxed mb-6">
                Your trusted partner for premium interior design solutions in
                Bangalore. We transform houses into dream homes.
              </p>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-brand-primary transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-brand-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-6">Our Services</h4>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.name}>
                    <Link
                      to={service.href}
                      className="text-gray-400 hover:text-brand-primary transition-colors"
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-bold mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="tel:+918147813339"
                    className="flex items-center gap-3 text-gray-400 hover:text-brand-primary transition-colors"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5" />
                    </div>
                    <span>+91 81478 13339</span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:contact@myinstaspaces.com"
                    className="flex items-center gap-3 text-gray-400 hover:text-brand-primary transition-colors"
                  >
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <span>contact@myinstaspaces.com</span>
                  </a>
                </li>
                <li>
                  <div className="flex items-start gap-3 text-gray-400">
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <span>218/1, Opp HDFC Bank, Kammanahalli Main Road, Kalyan Nagar, Bangalore - 560043</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-gray-400 text-sm hover:text-brand-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 text-sm hover:text-brand-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
